/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

/* eslint-disable no-duplicate-imports */
import { INodeProps } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { InputID, InputType, Suggestion } from '../nos-business-sign-up';

export interface IBusinessSignUpInputProps {
    id?: string;
    type: InputType;
    value?: string;
    pattern?: string;
    className: string;
    maxLength?: string;
    isRequired: boolean;
    fieldReference?: React.RefObject<HTMLInputElement>;
    onChange?(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, input?: string): void;
    isSubmittedPressed: boolean;
    suggestions?: [];
    selectedSuggestion?: string;
    onSuggestionSelect?: (suggestion: string) => void;
    updateCompanyAddress?(inputType: string, newValue: string): void;
}

export interface IBusinessSignUpInput {
    key: string;
    AddressItem: INodeProps;
    label: React.ReactNode;
    alert: React.ReactNode;
    input: React.ReactNode;
}

const GetMaxLength = (maxLength?: string): number | undefined => {
    if (maxLength) {
        const parsedMaxLength = Number.parseInt(maxLength, 10);
        if (!isNaN(parsedMaxLength)) {
            return parsedMaxLength;
        }
    }

    return undefined;
};

export const SignUpInput: React.FC<IBusinessSignUpInputProps> = ({
    id,
    type,
    value,
    pattern,
    className,
    maxLength,
    isRequired,
    onChange,
    fieldReference,
    isSubmittedPressed,
    suggestions,
    selectedSuggestion,
    onSuggestionSelect,
    updateCompanyAddress
}) => {
    const [inputValue, setInputValue] = React.useState<string | undefined>(value);
    const [showSuggestion, setShowSuggestion] = React.useState<boolean>(false);

    const autoSuggestFields = [InputID.CompanyName, InputID.Street];

    const listRef = React.useRef<HTMLUListElement>(null);

    React.useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (listRef.current && !listRef.current.contains(event.target as Node)) {
                setShowSuggestion(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [setShowSuggestion]);

    React.useEffect(() => {
        if (selectedSuggestion && suggestions !== undefined && suggestions?.length > 0) {
            const foundSuggestion: Suggestion = suggestions.find((suggestion: Suggestion) => suggestion.id === selectedSuggestion) || {
                id: ''
            };
            if (foundSuggestion && updateCompanyAddress) {
                switch (id) {
                    case InputID.CompanyName:
                        if (foundSuggestion?.poi?.name !== undefined) {
                            setInputValue(foundSuggestion?.poi?.name);
                            updateCompanyAddress(InputID.CompanyName, foundSuggestion?.poi?.name as string);
                        }
                        break;
                    case InputID.ZipCode:
                        setInputValue(foundSuggestion.address?.postalCode);
                        updateCompanyAddress(InputID.ZipCode, foundSuggestion?.address?.postalCode as string);
                        break;
                    case InputID.City:
                        if (foundSuggestion.address?.localName) {
                            setInputValue(foundSuggestion.address?.localName);
                            updateCompanyAddress(InputID.City, foundSuggestion.address?.localName as string);
                        } else if (foundSuggestion.address?.municipality) {
                            setInputValue(foundSuggestion.address?.municipality);
                            updateCompanyAddress(InputID.City, foundSuggestion?.address?.municipality as string);
                        }
                        break;
                    case InputID.Street:
                        setInputValue(foundSuggestion.address?.streetName);
                        updateCompanyAddress(InputID.Street, foundSuggestion?.address?.streetName as string);
                        break;
                    case InputID.HouseNumber:
                        setInputValue(foundSuggestion.address?.streetNumber ? foundSuggestion.address?.streetNumber : '');
                        updateCompanyAddress(InputID.HouseNumber, foundSuggestion?.address?.streetNumber as string);
                        break;
                }
            }
        }
    }, [selectedSuggestion]);

    if (type === InputType.TextArea) {
        return (
            <textarea
                id={id}
                name={id}
                value={value}
                className={`${className}-input-area ${className}-${id} ms-form__textarea`}
                aria-required={isRequired}
                maxLength={GetMaxLength(maxLength)}
                onChange={onChange}
            />
        );
    }

    const selectSuggestion = (fieldValue: string, addressId: string, showSuggestion: boolean = false) => {
        setInputValue(fieldValue);
        setShowSuggestion(showSuggestion);
        onSuggestionSelect?.(addressId);
    };

    const suggestionElement = (id: any, addressDisplay: string, fieldValue: string) => {
        if (fieldValue) {
            return (
                <li
                    key={id}
                    tabIndex={0}
                    onClick={() => {
                        selectSuggestion(fieldValue, id);
                    }}
                    onKeyDown={event => {
                        if (event.key === 'Enter') {
                            selectSuggestion(fieldValue, id);
                        }
                    }}
                    role='option'
                >
                    {addressDisplay}
                </li>
            );
        } else {
            return null;
        }
    };

    const formatAddress = (parts: any) => {
        return parts
            .filter((part: any) => part)
            .join(', ')
            .trim();
    };

    const suggestionOption = (suggestions: any) => (
        <ul className={`${className}-suggestions-list`} id='autocomplete-list' ref={listRef} role='listbox'>
            {suggestions.map((suggestion: any) => {
                switch (id) {
                    case InputID.CompanyName:
                        return suggestionElement(
                            suggestion.id,
                            formatAddress([
                                suggestion.poi?.name,
                                suggestion.address?.localName,
                                suggestion.address?.postalCode,
                                suggestion.address?.streetName,
                                suggestion.address?.streetNumber
                            ]),
                            suggestion.poi?.name
                        );
                    case InputID.Street:
                        return suggestionElement(
                            suggestion.id,
                            formatAddress([
                                suggestion.address?.streetName,
                                suggestion.address?.streetNumber,
                                suggestion.address?.localName,
                                suggestion.address?.postalCode
                            ]),
                            suggestion.address?.streetName
                        );
                    default:
                        return null;
                }
            })}
        </ul>
    );

    return (
        <>
            <input
                id={id}
                type={type}
                value={inputValue}
                className={`${className}-input ${className}-${id} ms-form__input ${isRequired &&
                    value === '' &&
                    isSubmittedPressed &&
                    'necessary'}`}
                pattern={pattern !== '' ? pattern : undefined}
                aria-required={isRequired}
                maxLength={GetMaxLength(maxLength)}
                onChange={e => {
                    setInputValue(e.target.value);
                    setShowSuggestion(autoSuggestFields.includes(id as InputID) ? true : false);
                    if (onChange) {
                        onChange(e);
                    }
                }}
                aria-autocomplete='list'
                aria-controls='autocomplete-list'
                ref={fieldReference}
            />
            {suggestions !== undefined && suggestions.length > 0 && showSuggestion && <>{suggestionOption(suggestions)}</>}
        </>
    );
};

export default SignUpInput;
